import * as React from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Box, Text, TextProps } from '@wix/design-system';
import { useTranslation } from 'react-i18next';

dayjs.extend(duration);

const initialValue = dayjs.duration(0);
interface CountdownClassNames {
  wrapper?: string;
  timePart?: string;
  label?: string;
  timePartWrapper?: string;
  separator?: string;
}

export interface CountdownProps {
  endDate: number;
  currentDate: number;
  classNames?: CountdownClassNames;
  timePartProps?: TextProps;
  separatorProps?: TextProps;
  timePartLabelProps?: TextProps;
}

const enum TimePartLabelKey {
  Hours = 'sale.countdown.hours',
  Minutes = 'sale.countdown.minutes',
  Seconds = 'sale.countdown.seconds',
}

const Separator: React.FC<{
  className?: string;
  separatorProps: TextProps;
}> = ({ className, separatorProps }) => (
  <Text className={className} size='small' {...separatorProps}>
    :
  </Text>
);

const TimePart: React.FC<{
  dataHook: string;
  value: number;
  label: TimePartLabelKey;
  classNames: CountdownClassNames;
  timePartProps: TextProps;
  timePartLabelProps?: TextProps;
}> = ({
  value,
  label,
  dataHook,
  classNames,
  timePartProps,
  timePartLabelProps,
}) => {
    const formattedValue = value < 10 ? `0${value}` : value;
    const { t } = useTranslation();

    return (
      <Box
        direction='vertical'
        align='center'
        className={classNames.timePartWrapper}
      >
        <Text
          dataHook={dataHook}
          className={classNames.timePart}
          {...timePartProps}
        >
          {formattedValue}
        </Text>
        <Text className={classNames.label} size='tiny' {...timePartLabelProps}>
          {t(label)}
        </Text>
      </Box>
    );
  };

export const Countdown: React.FC<CountdownProps> = ({
  endDate,
  currentDate,
  classNames = {
    timePart: '',
    label: '',
    separator: '',
  },
  timePartProps = {},
  separatorProps = {},
  timePartLabelProps = {},
}) => {
  const [timeLeft, setTimeLeft] = React.useState(
    dayjs.duration(dayjs(endDate).diff(dayjs(currentDate))),
  );

  React.useEffect(() => {
    const timeOut = setTimeout(
      () => setTimeLeft(timeLeft.subtract(1, 's')),
      1000,
    );
    return () => {
      clearTimeout(timeOut);
    };
  }, [timeLeft]);

  if (timeLeft.days() >= 1) {
    return null;
  }

  const counter = timeLeft.asMilliseconds() <= 0 ? initialValue : timeLeft;

  return (
    <Box dataHook='countdown' className={classNames.wrapper}>
      <Box>
        <TimePart
          dataHook='countdown-hours'
          value={counter.hours()}
          label={TimePartLabelKey.Hours}
          classNames={classNames}
          timePartProps={timePartProps}
          timePartLabelProps={timePartLabelProps}
        />
        <Separator
          className={classNames.separator}
          separatorProps={separatorProps}
        />
      </Box>
      <Box>
        <TimePart
          dataHook='countdown-minutes'
          value={counter.minutes()}
          label={TimePartLabelKey.Minutes}
          classNames={classNames}
          timePartProps={timePartProps}
          timePartLabelProps={timePartLabelProps}
        />
        <Separator
          className={classNames.separator}
          separatorProps={separatorProps}
        />
      </Box>
      <TimePart
        dataHook='countdown-seconds'
        value={counter.seconds()}
        label={TimePartLabelKey.Seconds}
        classNames={classNames}
        timePartProps={timePartProps}
        timePartLabelProps={timePartLabelProps}
      />
    </Box>
  );
};
